import { useEffect, useState, useRef } from 'react';
import { propertyService } from '../services/propertyService';

// Define the Property interface matching the database structure
export interface Property {
  id: string;
  address: string;
  details: any; // You can type this more specifically based on your data structure
}

/**
 * Custom hook to fetch saved properties for a user from Supabase.
 * @param userId - The ID of the user whose properties are to be fetched.
 * @returns An object containing the properties, loading state, and error state.
 */
export const useSavedProperties = (userId?: string) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Use a ref to track if we've already fetched for this userId to prevent duplicate fetches
  const fetchedUserIdRef = useRef<string | null>(null);
  
  // Use a ref to track if this is the initial mount
  const isInitialMount = useRef(true);

  useEffect(() => {
    // If no user ID is provided, skip fetching
    if (!userId) {
      setLoading(false);
      setProperties([]);
      return;
    }

    // Skip if this isn't the initial mount and we've already fetched for this userId
    if (!isInitialMount.current && fetchedUserIdRef.current === userId) {
      return;
    }
    
    // No longer the initial mount after the first run
    isInitialMount.current = false;

    const fetchProperties = async () => {
      try {
        setLoading(true);
        setError(null);

        let data;
        try {
          // Use the existing propertyService to fetch properties
          data = await propertyService.getProperties(userId);
          // Only log once when we actually receive data
          console.log('Received properties data:', data ? data.length : 0, 'properties');
        } catch (fetchError) {
          console.error('Error fetching properties:', fetchError);
          throw fetchError;
        }

        // Ensure data is not null and is an array before setting state
        if (Array.isArray(data)) {
          const formattedProperties = data.map((prop: any) => ({
            id: prop.id,
            address: prop.address,
            details: prop.zillow_data || {},
          }));
          setProperties(formattedProperties);
          // Update the ref to indicate we've fetched for this userId
          fetchedUserIdRef.current = userId;
        } else {
          setProperties([]);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [userId]); // Depend only on userId so that properties are refetched if userId changes

  return { properties, loading, error };
};
